import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/i18n/I18nProvider";
import { api, errorKey } from "@/lib/api";

const FIELDS = [
  { key: "legal_name", label: "legalName", full: true },
  { key: "address", label: "address", full: true },
  { key: "phone", label: "phone", type: "tel" },
  { key: "email", label: "email", type: "email" },
  { key: "website", label: "website" },
  { key: "tax_id", label: "taxId" },
  { key: "license_number", label: "license" },
];

export const SaveButton = ({ busy, testId }) => {
  const { t } = useI18n();
  return (
    <button type="submit" disabled={busy} data-testid={testId}
      className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-[#000B45] px-6 text-sm font-bold text-white transition-colors hover:bg-[#000624] disabled:opacity-70 sm:w-auto">
      {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? t("common.saving") : t("common.save")}
    </button>
  );
};

export const CompanyForm = () => {
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings/company").then((r) => setForm(r.data.settings)).catch((e) => setError(errorKey(e)));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return setError("validation.email");
    setBusy(true);
    setError("");
    try {
      const r = await api.put("/settings/company", { ...form, email: form.email || null });
      setForm(r.data.settings);
      toast.success(t("settings.saved"));
    } catch (err) {
      setError(errorKey(err));
    } finally {
      setBusy(false);
    }
  };

  if (!form) return error ? <p className="text-sm text-red-700">{t(error)}</p> : <div className="h-64 animate-pulse rounded-xl bg-white" />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} className="rounded-xl border border-[#E5E7EB] bg-white p-4 sm:p-6" data-testid="company-settings-form" noValidate>
      {!form.configured && (
        <p data-testid="company-not-configured" className="mb-5 rounded-lg border-l-4 border-[#FEB601] bg-[#FEB601]/10 px-4 py-3 text-sm text-[#111827]">
          {t("settings.notConfiguredBanner")}
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
        {FIELDS.map((f) => (
          <div key={f.key} className={`space-y-1.5 ${f.full ? "sm:col-span-2" : ""}`}>
            <Label htmlFor={f.key}>{t(`settings.${f.label}`)}</Label>
            <Input id={f.key} type={f.type || "text"} data-testid={`company-${f.key}-input`} value={form[f.key] || ""}
              onChange={set(f.key)} className="h-12 text-base" />
          </div>
        ))}
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="additional_info">{t("settings.additional")}</Label>
          <Textarea id="additional_info" data-testid="company-additional_info-input" rows={3} value={form.additional_info || ""}
            onChange={set("additional_info")} className="text-base" />
        </div>
      </div>
      {error && <p data-testid="company-error" className="mt-4 text-sm font-medium text-red-600">{t(error)}</p>}
      <div className="mt-6 flex justify-end"><SaveButton busy={busy} testId="company-save-button" /></div>
    </form>
  );
};
